import React from 'react';
import { Alert } from 'antd';
import { useViewerStore } from '../../../../../store/store';
import type { DashboardSectionPanelId } from '../dashboardSectionNav';
import { DashboardMainCardsSection } from './DashboardMainCardsSection';
import { DashboardAiRailSection } from './DashboardAiRailSection';
import { DashboardAuxiliarySection } from './DashboardAuxiliarySection';
import { ObdGoogleStreetViewProvider } from '../ObdGoogleStreetViewProvider';

interface DashboardReadyLayoutSectionProps {
    theme: 'dark' | 'light';
    siteAddress: any;
    titleSearchData: any;
    oppRefreshKey: number;
    setAddTitleContactsModalOpen: (next: boolean) => void;
    handleOpenBlackKnightLeadModal: () => void;
    dashboardAnnualUsage: number | null;
    dashboardSystemSizeKw: number | null;
    dashboardAvgDailyUsage: number | null;
    dashboardMultiplier: number | null;
    triggerPricingGridForDesign: (design: any) => void;
    priceQuote: any;
    isDashboardOpen: boolean;
    setIsDashboardOpen: (next: boolean | ((prev: boolean) => boolean)) => void;
    pricingFetchTrigger: number | null;
    pricingDesignForTrigger: any;
    setPricingFetchTrigger: React.Dispatch<React.SetStateAction<number | null>>;
    opportunityDetails: any;
    selectedDesignForPricing: any;
    setPricingGridModalOpen: (next: boolean) => void;
    opportunityID: any;
    aiRailOpen: boolean;
    setAiRailOpen: (next: boolean | ((prev: boolean) => boolean)) => void;
    setTreeCuttingPopupOpen: (next: boolean) => void;
    setTreeCuttingForLayoutKey: (next: string | null) => void;
    aiPanelTab: 'ai' | 'tree' | 'saved' | 'wishlist';
    setAiPanelTab: (next: 'ai' | 'tree' | 'saved' | 'wishlist') => void;
    slideRailContent: React.ReactNode;
    financePartnerSyncModalOpen: boolean;
    setFinancePartnerSyncModalOpen: (next: boolean) => void;
    leadSyncFormOpen: boolean;
    setLeadSyncFormOpen: (next: boolean) => void;
    initialDashboardReady: boolean;
    dashboardSectionPanel: DashboardSectionPanelId;
    setDashboardSectionPanel: React.Dispatch<React.SetStateAction<DashboardSectionPanelId>>;
    designsDashboardSlot: React.ReactNode;
    ahjTown: string;
    setbackNotes: string[];
    setAhjModalOpen: (next: boolean) => void;
    setOppValidationModalOpen: (next: boolean) => void;
    aiLoading?: boolean;
}

export const DashboardReadyLayoutSection: React.FC<DashboardReadyLayoutSectionProps> = (props) => {
    const {
        theme,
        siteAddress,
        titleSearchData,
        oppRefreshKey,
        setAddTitleContactsModalOpen,
        handleOpenBlackKnightLeadModal,
        dashboardAnnualUsage,
        dashboardSystemSizeKw,
        dashboardAvgDailyUsage,
        dashboardMultiplier,
        triggerPricingGridForDesign,
        priceQuote,
        isDashboardOpen,
        setIsDashboardOpen,
        pricingFetchTrigger,
        pricingDesignForTrigger,
        setPricingFetchTrigger,
        opportunityDetails,
        selectedDesignForPricing,
        setPricingGridModalOpen,
        opportunityID,
        aiRailOpen,
        setAiRailOpen,
        setTreeCuttingPopupOpen,
        setTreeCuttingForLayoutKey,
        aiPanelTab,
        setAiPanelTab,
        slideRailContent,
        financePartnerSyncModalOpen,
        setFinancePartnerSyncModalOpen,
        leadSyncFormOpen,
        setLeadSyncFormOpen,
        initialDashboardReady,
        dashboardSectionPanel,
        setDashboardSectionPanel,
        designsDashboardSlot,
        ahjTown,
        setbackNotes,
        setAhjModalOpen,
        setOppValidationModalOpen,
    } = props;

    const siteLoadingError = useViewerStore((s) => s.siteLoadingError);

    return (
        <ObdGoogleStreetViewProvider theme={theme}>
        <>
            {siteLoadingError ? (
                <div className={`obd-site-load-error-banner obd-theme-${theme}`}>
                    <Alert
                        type="error"
                        showIcon
                        closable={false}
                        message="Site loading failed"
                        description={
                            <span style={{ whiteSpace: 'pre-wrap' }}>{siteLoadingError}</span>
                        }
                    />
                </div>
            ) : null}
            <DashboardMainCardsSection
                theme={theme}
                siteAddress={siteAddress}
                titleSearchData={titleSearchData}
                oppRefreshKey={oppRefreshKey}
                setAddTitleContactsModalOpen={setAddTitleContactsModalOpen}
                handleOpenBlackKnightLeadModal={handleOpenBlackKnightLeadModal}
                dashboardAnnualUsage={dashboardAnnualUsage}
                dashboardSystemSizeKw={dashboardSystemSizeKw}
                dashboardAvgDailyUsage={dashboardAvgDailyUsage}
                dashboardMultiplier={dashboardMultiplier}
                triggerPricingGridForDesign={triggerPricingGridForDesign}
                priceQuote={priceQuote}
                isDashboardOpen={isDashboardOpen}
                pricingFetchTrigger={pricingFetchTrigger}
                pricingDesignForTrigger={pricingDesignForTrigger}
                setPricingFetchTrigger={setPricingFetchTrigger}
                opportunityDetails={opportunityDetails}
                selectedDesignForPricing={selectedDesignForPricing}
                setPricingGridModalOpen={setPricingGridModalOpen}
                opportunityID={opportunityID}
                dashboardSectionPanel={dashboardSectionPanel}
                setDashboardSectionPanel={setDashboardSectionPanel}
                designsDashboardSlot={designsDashboardSlot}
                ahjTown={ahjTown}
                setbackNotes={setbackNotes}
                setAhjModalOpen={setAhjModalOpen}
                setOppValidationModalOpen={setOppValidationModalOpen}
                setIsDashboardOpen={setIsDashboardOpen}
                initialDashboardReady={initialDashboardReady}
            />

            <DashboardAiRailSection
                aiRailOpen={aiRailOpen}
                setAiRailOpen={setAiRailOpen}
                setTreeCuttingPopupOpen={setTreeCuttingPopupOpen}
                setTreeCuttingForLayoutKey={setTreeCuttingForLayoutKey}
                isDashboardOpen={isDashboardOpen}
                theme={theme}
                aiPanelTab={aiPanelTab}
                setAiPanelTab={setAiPanelTab}
                aiLoading={props.aiLoading}
            >
                {slideRailContent}
            </DashboardAiRailSection>

            <DashboardAuxiliarySection
                financePartnerSyncModalOpen={financePartnerSyncModalOpen}
                setFinancePartnerSyncModalOpen={setFinancePartnerSyncModalOpen}
                leadSyncFormOpen={leadSyncFormOpen}
                setLeadSyncFormOpen={setLeadSyncFormOpen}
            />
        </>
        </ObdGoogleStreetViewProvider>
    );
};
